import React, { useState, useEffect } from 'react';
import './SpaceComm.css';

/**
 * Parse CSV string into array of objects
 */
const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.trim());
  
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = values[index] || '';
    });
    return obj;
  });
};

/**
 * SpaceComm Component
 * A floating icon button that opens a full-screen menu with project cards.
 * This component is designed to be portable and can be copied into any project.
 * 
 * Props:
 * - iconSrc: Path to the floating icon image (default: '/sc/DSL-Comms-Badge.svg')
 * - csvPath: Path to CSV file with project data (default: '/sc/projects.csv')
 */
const SpaceComm = ({ 
  iconSrc = '/sc/DSL-Comms-Badge.svg',
  csvPath = '/sc/projects.csv'
}) => {
const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load projects from CSV
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch(csvPath);
        const csvText = await response.text();
        const parsedProjects = parseCSV(csvText);
        
        // Transform to add full image path
        const projectsWithPaths = parsedProjects.map(project => ({
          ...project,
          screenshot: `/sc/${project.screenshot}`
        }));
        
        setProjects(projectsWithPaths);
      } catch (error) {
        console.error('Failed to load SpaceComm projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadProjects();
  }, [csvPath]);

  // Check if URL has the /?sc=true suffix to show the button
  const shouldShowButton = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sc') === 'true';
  };

  const handleIconClick = () => {
    window.location.href = 'https://space.darkstarlabs.io';
  };

const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

const handleLaunchClick = (e, url) => {
    e.stopPropagation();
    window.location.href = url;
  };

  // Don't render if URL doesn't have the suffix
  if (!shouldShowButton()) {
    return null;
  }

  return (
    <>
      {/* Floating Icon Button */}
      <button 
        className="spacecomm-icon-button" 
        onClick={handleIconClick}
        aria-label="Open SpaceComm Menu"
      >
        <img src={iconSrc} alt="SpaceComm" className="spacecomm-icon-image" />
      </button>

      {/* Full Screen Menu */}
      {isMenuOpen && (
        <div className="spacecomm-overlay">
          {/* DSL Logo - above menu */}
          <img 
            src="/sc/DSL Logo Stacked Light and Orange.svg" 
            alt="Dark Star Labs" 
            className="spacecomm-logo"
          />
          
          <div className="spacecomm-menu">
            {/* Close Button */}
            <button 
              className="spacecomm-close-button" 
              onClick={handleCloseMenu}
              aria-label="Close Menu"
            >
              ×
            </button>

            {/* Menu Content */}
            <div className="spacecomm-content">
<p className="spacecomm-subtitle">
                <span className="spacecomm-subtitle-bold">Click launch</span> to open a new experience.
              </p>
              
              {/* Project Cards */}
              <div className="spacecomm-cards-container">
                {loading ? (
                  <p style={{ color: 'rgba(100, 200, 255, 0.9)', textAlign: 'center' }}>Loading projects...</p>
                ) : projects.length === 0 ? (
                  <p style={{ color: 'rgba(100, 200, 255, 0.9)', textAlign: 'center' }}>No projects found.</p>
                ) : (
                  projects.map((project, index) => (
                    <div 
                      key={index}
                      className="spacecomm-card"
                    >
<div 
                        className="spacecomm-card-image-wrapper"
                      >
                        <img 
                          src={project.screenshot} 
                          alt={project.title}
                          className="spacecomm-card-image"
                        />
                      </div>
                      <div className="spacecomm-card-content">
                        <h3 className="spacecomm-card-title">{project.title}</h3>
                        <p className="spacecomm-card-description">{project.description}</p>
                        <button 
                          className="spacecomm-launch-button"
                          onClick={(e) => handleLaunchClick(e, project.url)}
                        >
                          Launch
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Tagline - below menu */}
          <h2 className="spacecomm-title">
            We make space <span className="spacecomm-title-highlight">interactive</span>.
          </h2>
        </div>
      )}
    </>
  );
};

export default SpaceComm;
